/**
 * bulk-password-parse.ts — client-side parser for the admin "Bulk password update" dialog.
 *
 * Input is pasted text or a CSV file, one account per line. Accepted shapes:
 *   email,password              (also `;`, TAB or whitespace as the separator)
 *   email,password,...          with a header line naming the columns — only `email` and
 *                               `password` are used, any other columns are ignored
 * Blank lines and lines starting with `#` are skipped. A UTF-8 BOM and CRLF are tolerated.
 *
 * Without a header ("pair mode") the FIRST field is the email and EVERYTHING after the
 * separator is the password, so passwords containing commas or spaces survive; a password
 * wrapped in double quotes is unwrapped. With a header, fields are split RFC-4180-style for
 * the detected delimiter.
 *
 * Rows are matched to CRM accounts by email (case-insensitive). Every row gets a status so the
 * dialog can show exactly what will run and what needs attention before anything is sent.
 *
 * SECURITY: pure function — no I/O, no logging. Passwords live only in the returned rows
 * (component state) and are sent ONE ACCOUNT AT A TIME to POST /accounts/:id/password.
 */

export type BulkRowStatus =
  | 'ready'
  | 'unknown_email'
  | 'duplicate'
  | 'missing_password'
  | 'invalid_line';

export interface BulkAccountRef {
  id: string;
  email: string;
  displayLabel: string;
}

export interface BulkPasswordRow {
  /** 1-based line number in the pasted text (stable key for run results). */
  lineNo: number;
  /** Trimmed, lower-cased email as pasted. */
  email: string;
  /** Password as pasted (trimmed); '' when missing. */
  password: string;
  status: BulkRowStatus;
  /** The matched CRM account, or null when unknown / invalid. */
  account: BulkAccountRef | null;
}

export interface BulkPasswordParseResult {
  rows: BulkPasswordRow[];
  /** Rows that will be sent. */
  ready: number;
  /** Rows that need the operator's attention (every non-ready status). */
  problems: number;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Delimiter preference for header mode: a TAB beats a comma beats a semicolon. */
const DELIMITERS = ['\t', ',', ';'] as const;

function detectDelimiter(line: string): string | null {
  for (const d of DELIMITERS) {
    if (line.includes(d)) return d;
  }
  return null;
}

/** RFC-4180-ish split of ONE line: double-quote wrapping, `""` escapes, fields trimmed. */
export function splitDelimited(line: string, delimiter: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
    } else if (ch === '"' && cur.trim().length === 0) {
      cur = '';
      inQuotes = true;
    } else if (ch === delimiter) {
      out.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out.map((f) => f.trim());
}

/** Unwrap a double-quoted value (pair mode); otherwise return it trimmed. */
function unquote(value: string): string {
  const t = value.trim();
  if (t.length >= 2 && t.startsWith('"') && t.endsWith('"')) {
    return t.slice(1, -1).replace(/""/g, '"');
  }
  return t;
}

interface HeaderMap {
  delimiter: string; // ' ' = whitespace-separated
  emailIdx: number;
  passwordIdx: number;
}

/** A header line names BOTH an `email` and a `password` column (any order, any extras). */
function detectHeader(line: string): HeaderMap | null {
  const delimiter = detectDelimiter(line) ?? ' ';
  const fields = (delimiter === ' ' ? line.split(/\s+/) : splitDelimited(line, delimiter)).map(
    (f) => f.toLowerCase(),
  );
  const emailIdx = fields.indexOf('email');
  const passwordIdx = fields.indexOf('password');
  if (emailIdx === -1 || passwordIdx === -1) return null;
  return { delimiter, emailIdx, passwordIdx };
}

export function parseBulkPasswordInput(
  text: string,
  accounts: readonly BulkAccountRef[],
): BulkPasswordParseResult {
  const byEmail = new Map<string, BulkAccountRef>();
  for (const a of accounts) byEmail.set(a.email.trim().toLowerCase(), a);

  const lines = text.replace(/^﻿/, '').split(/\r\n|\r|\n/);
  const rows: BulkPasswordRow[] = [];
  const seen = new Set<string>();
  let header: HeaderMap | null = null;
  let sawContent = false;

  for (let i = 0; i < lines.length; i++) {
    const line = (lines[i] ?? '').trim();
    if (!line || line.startsWith('#')) continue;

    if (!sawContent) {
      sawContent = true;
      const h = detectHeader(line);
      if (h) {
        header = h;
        continue;
      }
    }

    const lineNo = i + 1;
    let email = '';
    let password = '';

    if (header) {
      const fields =
        header.delimiter === ' ' ? line.split(/\s+/) : splitDelimited(line, header.delimiter);
      email = (fields[header.emailIdx] ?? '').trim();
      password = (fields[header.passwordIdx] ?? '').trim();
    } else {
      // Pair mode: first field = email, everything after the separator = password.
      const sep = /[\t,;\s]/.exec(line);
      if (!sep) {
        email = line;
      } else {
        email = line.slice(0, sep.index).trim();
        let rest = line.slice(sep.index + 1);
        // "email , password" / "email ;password": when the first separator was whitespace,
        // swallow ONE optional delimiter (and spaces) so the password does not start with it.
        // A real delimiter as the first separator is never touched, so `a@b.com,;x` keeps `;x`.
        if (/\s/.test(sep[0])) rest = rest.replace(/^\s*[,;\t]?\s*/, '');
        password = unquote(rest);
      }
    }

    email = email.toLowerCase();
    const push = (status: BulkRowStatus, account: BulkAccountRef | null): void => {
      rows.push({ lineNo, email, password, status, account });
    };

    if (!EMAIL_RE.test(email)) {
      push('invalid_line', null);
      continue;
    }
    if (!password) {
      push('missing_password', byEmail.get(email) ?? null);
      continue;
    }
    if (seen.has(email)) {
      push('duplicate', byEmail.get(email) ?? null);
      continue;
    }
    seen.add(email);
    const account = byEmail.get(email);
    if (!account) {
      push('unknown_email', null);
      continue;
    }
    push('ready', account);
  }

  const ready = rows.filter((r) => r.status === 'ready').length;
  return { rows, ready, problems: rows.length - ready };
}
